import { areAllShipmentsAssigned } from '@/helpers/areAllShipmentsAssigned'
import { XsrfResilient } from '@/services/TypedHelpers'
import { error, log } from '@/services/Log'
import { assertBCCheckout, BCCheckoutType } from '@/types/BigCommerce/BCCheckout'
import type { OrderStatus, StandardCart, StandardOrder } from '@/types/ShopFront/CheckoutStandards'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'

const ORDER_STATUS_INCOMPLETE = 'INCOMPLETE'

export type Ordergetter = (input: { cart : StandardCart }) => Promise<StandardOrder>

export const getOrder: Ordergetter = async ({ cart, cart: { id: checkoutID } }) => {
  log('getOrder START', { cart, checkoutID })
  const [
    { default: Axios },
    { finalizePendingOrder },
    { getOrderStandardizer },
    { augmentConsignments },
    { standardizeAddressStorefrontCheckoutApi },
    { calculateTotalDiscountFromLineItems },
  ] = await allPromisesWithRetries(() => [
    import('axios'),
    import('./finalizePendingOrder'),
    import('@/services/Standardizers/standardizeOrder'),
    import('@/services/Standardizers/augmentConsignments'),
    import('@/services/Standardizers/standardizeAddressStorefrontCheckoutApi'),
    import('@/helpers/checkoutHelpers/calculateTotalDiscountFromLineItems'),
  ])
  // Returns a Checkout. - https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout/checkoutsbycheckoutidget
  const { data: order } = await Axios.get<BCCheckoutType>(`/api/storefront/checkouts/${checkoutID}?include=consignments.availableShippingOptions`)
  assertBCCheckout(order)
  // default status, can be overridden by real status
  const status: OrderStatus = ORDER_STATUS_INCOMPLETE

  const standardizeOrder = getOrderStandardizer({
    augmentConsignments,
    standardizeAddressStorefrontCheckoutApi,
    calculateTotalDiscountFromLineItems,
  })
  const newOrder = await standardizeOrder({
    ...order,
    status,
    customerId: order.cart.customerId,
  }, cart)
  // Checking if the order has been finalized
  if (order?.orderId) {
    try {
      await finalizePendingOrder({
        order: {
          ...newOrder,
          orderId: String(order.orderId),
        },
      })
    } catch (err) {
      error('Failed to initialize pendig order', err)
    }
  }
  log('getOrder END', { newOrder, areAllShipmentsAssigned: areAllShipmentsAssigned(newOrder) })
  return newOrder
}

const XsrfResilientGetOrder = XsrfResilient(getOrder)

export default XsrfResilientGetOrder
